import React from "react";
import { Box, Card, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

export type StatCardProps = {
  label: string;
  value: React.ReactNode;
  hint?: string;
  icon?: React.ReactNode;
  /** Semantic accent: drives the icon chip + delta color. */
  color?: "primary" | "success" | "warning" | "error" | "secondary";
  /** Signed period-over-period change; renders a colored pill when provided. */
  delta?: number;
  deltaSuffix?: string;
};

/**
 * KPI card (Nektia "Total questions / Purchase intent" style): big number,
 * label, icon chip, and an optional trend pill.
 *
 * Contrast: the icon glyph and delta text use the palette's per-mode readable
 * shade (`.dark` on light backgrounds, `.light` on dark) so colored text on a
 * tinted chip always clears WCAG AA — the raw `.main` hue is reserved for the
 * low-alpha fill only.
 */
export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  hint,
  icon,
  color = "primary",
  delta,
  deltaSuffix = "",
}) => {
  const theme = useTheme();
  const isLight = theme.palette.mode === "light";
  const readable = (key: StatCardProps["color"] & string) =>
    isLight ? theme.palette[key].dark : theme.palette[key].light;

  const accentMain = theme.palette[color].main;
  const accentText = readable(color);

  const hasDelta = typeof delta === "number" && !Number.isNaN(delta);
  const positive = (delta ?? 0) >= 0;
  const deltaKey = positive ? "success" : "error";
  const deltaMain = theme.palette[deltaKey].main;
  const deltaText = readable(deltaKey);

  return (
    <Card
      elevation={0}
      sx={{
        p: 2.25,
        height: "100%",
        position: "relative",
        overflow: "hidden",
        transition: "transform 180ms ease, border-color 180ms ease",
        "&:hover": { transform: "translateY(-2px)", borderColor: alpha(accentMain, 0.6) },
      }}
    >
      {/* subtle accent wash in the corner */}
      <Box
        sx={{
          position: "absolute",
          top: -28,
          right: -28,
          width: 96,
          height: 96,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(accentMain, isLight ? 0.12 : 0.2)} 0%, transparent 70%)`,
          pointerEvents: "none",
        }}
      />
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "text.secondary" }}>
          {label}
        </Typography>
        {icon && (
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(accentMain, isLight ? 0.12 : 0.18),
              color: accentText,
            }}
          >
            {icon}
          </Box>
        )}
      </Stack>

      <Typography
        sx={{ fontSize: 30, fontWeight: 800, lineHeight: 1.1, mt: 1.25, color: "text.primary" }}
      >
        {value}
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
        {hasDelta && (
          <Box
            component="span"
            sx={{
              fontSize: 11,
              fontWeight: 700,
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              color: deltaText,
              bgcolor: alpha(deltaMain, isLight ? 0.13 : 0.2),
            }}
          >
            {positive ? "▲" : "▼"} {Math.abs(delta as number)}
            {deltaSuffix}
          </Box>
        )}
        {hint && (
          <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{hint}</Typography>
        )}
      </Stack>
    </Card>
  );
};
